import { Component, Inject, OnInit } from '@angular/core';
import {
  FormGroup,
  FormControl,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';

import { MerchantService, RewardService } from 'src/app/services';
import { CardType } from '../../../models/stamp_card';
import { Reward } from '../../../models/reward';
import { Customer } from '../../../models/customer';
import { Constants } from 'src/app/app.constants';

@Component({
  selector: 'adjust-points-dialog',
  templateUrl: './adjust-points-dialog.component.html',
  styleUrls: ['./adjust-points-dialog.component.css'],
})
export class AdjustPointsDialogComponent implements OnInit {
  form!: FormGroup;
  pointsFormControl!: FormControl;
  adjustmentModeFormControl!: FormControl;
  rewardFormControl!: FormControl;
  redeemImmediatelyFormControl!: FormControl;
  cardSelectionFormControl!: FormControl;
  customerFormControl!: FormControl;

  loading = false;
  error = false;
  rewardsLoading = false;
  cardsLoading = false;
  customersLoading = false;

  // Customer selection
  customers: Customer[] = [];
  selectedCustomer: Customer | null = null;
  showCustomerSelection = false;

  currentPoints = 0;
  maxPoints = 0;
  cardTitle = '';
  customerName = '';

  // Card selection
  availableCards: any[] = [];
  selectedUserCard: any = null;
  showCardSelection = false;

  // Adjustment modes
  adjustmentModes = [
    { value: 'simple', label: 'Simple Adjustment' },
    { value: 'convert', label: 'Convert Points to Rewards' },
  ];

  // Rewards data
  rewards: Reward[] = [];
  cardRewards: any[] = []; // Card's redeemable rewards configuration
  selectedReward: Reward | null = null;
  selectedCardReward: any = null; // The card's reward configuration
  pointsNeededForReward = 0;

  constructor(
    public matDialogRef: MatDialogRef<AdjustPointsDialogComponent>,
    public fb: FormBuilder,
    private constants: Constants,
    private merchantService: MerchantService,
    private rewardService: RewardService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    const customer = this.data.customer;

    if (!customer) {
      // No customer provided - show customer selection
      this.showCustomerSelection = true;
      this.initializeFormControls();
      this.getCustomers();
      return;
    }

    this.selectedCustomer = customer;
    this.customerName = this.getCustomerName(customer);

    // Check if we have multiple cards or a single card
    if (this.data.userCards && this.data.userCards.length > 0) {
      // Multiple cards provided - show card selection
      this.processUserCards(this.data.userCards);
    } else if (this.data.userCard) {
      // Single card provided
      this.selectedUserCard = this.data.userCard;
      this.showCardSelection = false;
      this.initializeFormControls();
      this.setupFormListeners();
      this.updateCardData();
      this.getRewards();
    } else {
      // No cards provided - fetch them from the API
      this.fetchUserCards();
    }
  }

  fetchUserCards(): void {
    if (!this.data.customer?._id) {
      this.constants.snack('Customer information not available');
      return;
    }

    this.cardsLoading = true;
    this.merchantService
      .getCustomerCards(100, 0, this.data.customer._id) // Get up to 100 cards
      .subscribe({
        next: (res: any) => {
          if (res && res.docs) {
            const pointsCards = res.docs.filter((userCard: any) => {
              const stampCard = this.getStampCard(userCard);
              return (
                stampCard &&
                typeof stampCard !== 'string' &&
                stampCard.cardType === CardType.openEndedPoint
              );
            });

            if (pointsCards.length === 0) {
              this.matDialogRef.close();
              return;
            }

            this.processUserCards(pointsCards);
          } else {
            this.constants.snack('No cards found for this customer');
            this.matDialogRef.close();
          }
        },
        error: (error: any) => {
          this.constants.snack('Failed to load customer cards');
          this.matDialogRef.close();
        },
      })
      .add(() => {
        this.cardsLoading = false;
      });
  }

  processUserCards(userCards: any[]): void {
    this.availableCards = userCards.map((userCard: any) => {
      const stampCard = this.getStampCard(userCard);
      return {
        ...userCard,
        cardTitle:
          (stampCard && typeof stampCard !== 'string'
            ? stampCard.title
            : null) || 'Unknown Card',
        info: `${userCard.numberOfPoints || 0} points`,
      };
    });

    this.selectedUserCard = this.availableCards[0];
    this.showCardSelection = true;

    this.initializeFormControls();
    this.setupFormListeners();
    this.updateCardData();
    this.getRewards();
  }

  getCustomers(): void {
    this.customersLoading = true;
    this.merchantService
      .getCustomers('_id', 'desc', 5000, 0, '', {})
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.customers = res.docs;

            this.customers.forEach((el) => {
              if (typeof el.user !== 'string') {
                el.name = el.user?.name ?? el?.name;
                el.email = el.user?.email ?? el?.email;
              }
            });

            this.customers.sort((a, b) => a.name!.localeCompare(b.name!));
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.customersLoading = false));
  }

  onCustomerSelected(customer: Customer): void {
    this.selectedCustomer = customer;
    this.customerName = this.getCustomerName(customer);

    // Reset card selection
    this.availableCards = [];
    this.selectedUserCard = null;
    this.showCardSelection = false;

    if (customer) {
      // Fetch cards for the selected customer
      this.fetchUserCardsForCustomer(customer._id!);
    }
  }

  fetchUserCardsForCustomer(customerId: string): void {
    this.cardsLoading = true;
    this.merchantService
      .getCustomerCards(100, 0, customerId)
      .subscribe({
        next: (res: any) => {
          if (res && res.docs) {
            const pointsCards = res.docs.filter((userCard: any) => {
              const stampCard = this.getStampCard(userCard);
              return (
                stampCard &&
                typeof stampCard !== 'string' &&
                stampCard.cardType === CardType.openEndedPoint
              );
            });

            if (pointsCards.length === 0) {
              this.constants.snack(
                'This customer has no open ended points cards',
              );
              return;
            }

            this.processUserCards(pointsCards);
          } else {
            this.constants.snack('No cards found for this customer');
          }
        },
        error: (error: any) => {
          this.constants.snack('Failed to load customer cards');
        },
      })
      .add(() => {
        this.cardsLoading = false;
      });
  }

  initializeFormControls(): void {
    this.pointsFormControl = new FormControl(0, [
      Validators.required,
      Validators.min(0),
      Validators.pattern(/^\d+$/), // Only whole numbers
    ]);

    this.adjustmentModeFormControl = new FormControl(null, [
      Validators.required,
    ]);

    this.rewardFormControl = new FormControl(null);
    this.redeemImmediatelyFormControl = new FormControl(false);
    this.cardSelectionFormControl = new FormControl(this.selectedUserCard);
    this.customerFormControl = new FormControl(
      this.selectedCustomer,
      this.showCustomerSelection ? [Validators.required] : [],
    );

    this.form = this.fb.group({
      points: this.pointsFormControl,
      adjustmentMode: this.adjustmentModeFormControl,
      reward: this.rewardFormControl,
      redeemImmediately: this.redeemImmediatelyFormControl,
      cardSelection: this.cardSelectionFormControl,
      customer: this.customerFormControl,
    });
  }

  updateCardData(): void {
    if (!this.selectedUserCard) return;

    const card = this.getStampCard(this.selectedUserCard);

    this.currentPoints = this.selectedUserCard?.numberOfPoints ?? 0;
    this.cardTitle = card?.title ?? 'Unknown Card';
    this.cardRewards = card?.rewards || [];

    if (card?.cardType === CardType.openEndedPoint) {
      this.maxPoints = card?.points?.maxPoints ?? 999999;
    } else {
      this.maxPoints = 999999; // No limit for other card types
    }

    // Update form validators
    this.pointsFormControl.setValidators([
      Validators.required,
      Validators.min(0),
      Validators.max(this.maxPoints),
      Validators.pattern(/^\d+$/),
    ]);
    this.pointsFormControl.setValue(this.currentPoints);
    this.pointsFormControl.updateValueAndValidity();
  }

  getStampCard(userCard: any): any {
    return userCard.card && typeof userCard.card !== 'string'
      ? Array.isArray(userCard.card)
        ? userCard.card[0]
        : userCard.card
      : null;
  }

  getCustomerName(customer: any): string {
    if (customer?.user?.name) {
      return customer.user.name;
    }
    if (customer?.user?.email) {
      return customer.user.email;
    }
    if (customer?.name) {
      return customer.name;
    }
    if (customer?.email) {
      return customer.email;
    }
    return 'Unknown Customer';
  }

  canSubmit(): boolean {
    if (
      this.loading ||
      this.rewardsLoading ||
      this.cardsLoading ||
      this.customersLoading
    ) {
      return false;
    }

    // If customer selection is required, ensure customer is selected
    if (this.showCustomerSelection && !this.selectedCustomer) {
      return false;
    }

    // Ensure we have a selected card
    if (!this.selectedUserCard) {
      return false;
    }

    const isFormValid = this.form.valid;

    if (this.isConvertMode) {
      return isFormValid && this.canConvertToReward;
    }

    return isFormValid;
  }

  primaryTapped() {
    if (!this.canSubmit()) {
      this.form.markAllAsTouched();
      return;
    }

    const adjustmentMode = this.adjustmentModeFormControl.value;
    const newPoints = this.pointsFormControl.value;

    if (!Number.isInteger(newPoints) || newPoints < 0) {
      this.constants.snack('Points must be a non-negative whole number');
      return;
    }

    if (newPoints > this.maxPoints) {
      this.constants.snack(`Points cannot exceed ${this.maxPoints}`);
      return;
    }

    if (adjustmentMode?.value === 'convert') {
      this.adjustPoints();
    } else {
      if (newPoints === this.currentPoints) {
        this.matDialogRef.close();
        return;
      }
      this.adjustPoints(newPoints);
    }
  }

  adjustPoints(newPoints?: number) {
    let shouldRedeem = false;

    if (this.isConvertMode) {
      shouldRedeem = this.redeemImmediatelyFormControl?.value;

      if (!this.selectedReward) {
        this.constants.snack('Please select a reward to convert points to');
        return;
      }

      if (!this.canConvertToReward) {
        this.constants.snack('Insufficient points for selected reward');
        return;
      }

      if (!newPoints) {
        newPoints = this.currentPoints - this.selectedCardReward.points;
      }
    }

    this.loading = true;
    this.error = false;

    const customerId = this.selectedCustomer?._id || this.data.customer?._id;

    const data = {
      customerId: customerId,
      userCardId: this.selectedUserCard._id,
      newPoints: newPoints!,
      isConvertMode: this.isConvertMode,
      shouldRedeem: shouldRedeem,
      rewardId: this.selectedReward?._id,
    };

    this.merchantService
      .adjustPoints(data)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.matDialogRef.close({ success: true, newPoints: newPoints });
          }
        },
        error: (error: any) => {
          this.error = true;
          this.constants.snack(
            error?.error?.message || 'Failed to adjust points',
          );
        },
      })
      .add(() => {
        this.loading = false;
      });
  }

  setupFormListeners(): void {
    this.adjustmentModeFormControl.valueChanges.subscribe((mode) => {
      this.onAdjustmentModeChange(mode);
    });

    this.rewardFormControl.valueChanges.subscribe((reward) => {
      this.onRewardSelectionChange(reward);
    });

    if (this.showCardSelection) {
      this.cardSelectionFormControl.valueChanges.subscribe((selectedCard) => {
        this.onCardSelectionChange(selectedCard);
      });
    }
  }

  onCardSelectionChange(selectedCard: any): void {
    if (selectedCard && selectedCard !== this.selectedUserCard) {
      this.selectedUserCard = selectedCard;
      this.updateCardData();

      // Reset form state when card changes
      this.adjustmentModeFormControl.setValue(null);
      this.rewardFormControl.setValue(null);
      this.selectedReward = null;
      this.pointsNeededForReward = 0;

      // Reload rewards for the new card
      this.getRewards();
    }
  }

  onAdjustmentModeChange(mode: any): void {
    const isConvertMode = mode?.value === 'convert';

    if (isConvertMode) {
      this.rewardFormControl.setValidators([Validators.required]);
    } else {
      this.rewardFormControl.clearValidators();
      this.rewardFormControl.setValue(null);
      this.selectedReward = null;
      this.pointsNeededForReward = 0;

      this.pointsFormControl.setValue(this.currentPoints);
    }
    this.rewardFormControl.updateValueAndValidity();
  }

  onRewardSelectionChange(reward: Reward | null): void {
    this.selectedReward = reward;
    this.selectedCardReward = null;

    if (reward) {
      this.selectedCardReward = this.cardRewards.find((cr) => {
        const rewardId =
          typeof cr.reward === 'string' ? cr.reward : cr.reward?._id;
        return rewardId === reward._id;
      });

      if (this.selectedCardReward && this.selectedCardReward.points) {
        this.pointsNeededForReward = this.selectedCardReward.points;

        const newPointsTotal =
          this.currentPoints - this.selectedCardReward.points;
        this.pointsFormControl.setValue(Math.max(0, newPointsTotal));
      } else {
        this.pointsNeededForReward = 0;
        this.pointsFormControl.setValue(this.currentPoints);
      }
    } else {
      this.pointsNeededForReward = 0;
      this.pointsFormControl.setValue(this.currentPoints);
    }
  }

  getRewards(): void {
    this.rewardsLoading = true;
    this.rewardService
      .getRewards(1000, 0)
      .subscribe({
        next: (res: any) => {
          if (res) {
            const cardRewardIds = this.cardRewards.map((cr) =>
              typeof cr.reward === 'string' ? cr.reward : cr.reward?._id,
            );

            this.rewards = res.docs.filter(
              (reward: Reward) =>
                reward.active && cardRewardIds.includes(reward._id),
            );
            this.rewards.sort((a, b) => a.title!.localeCompare(b.title!));

            this.rewards.map((r) => {
              const cardReward = this.cardRewards.find(
                (el) => el.reward === r._id,
              );
              if (cardReward) {
                r._points = `${cardReward.points} points required`;
              }
            });
          }
        },
        error: (error: any) => {
          this.constants.snack('Failed to load rewards');
        },
      })
      .add(() => (this.rewardsLoading = false));
  }

  get isConvertMode(): boolean {
    return this.adjustmentModeFormControl?.value?.value === 'convert';
  }

  get canConvertToReward(): boolean {
    return !!(
      this.selectedReward &&
      this.selectedCardReward &&
      this.selectedCardReward.points &&
      this.selectedCardReward.points <= this.currentPoints
    );
  }
}
